import React from 'react';

import { T } from '../customer/customers.theme';
import { COPY } from './settings.copy';
import {
  Card,
  EmptyNote,
  Field,
  FieldError,
  LABEL_HEIGHT,
  PlusGlyph,
  RowBin,
  SectionHead,
  SmallButton,
  TYPE,
} from './SettingsUI';

/**
 * A card holding a list of rows that can be added to and taken from.
 *
 * Payment accounts and saved SMS messages are the same screen twice: a short
 * list of two-field rows, each removable, with an Add at the bottom and a cap
 * on how many there can be. Written once, because the day one of them gets a
 * fix the other needs it too.
 *
 * Column headings appear above the first row only, so eight accounts do not
 * repeat "Name / Number" eight times.
 */
export default function RowsCard({
  title,
  body,
  empty,
  addLabel,
  fullNote,
  footNote,
  columns,
  rows,
  errors,
  showErrors,
  busy,
  full,
  idPrefix,
  onChange,
  onAdd,
  onRemove,
}) {
  return (
    <Card>
      <SectionHead title={title} body={body} />

      {rows.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rows.map((row, index) => {
            const first = index === 0;
            const problem = showErrors ? errors[index] : '';

            return (
              <div key={row.key}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    flexWrap: 'wrap',
                  }}
                >
                  {columns.map((column) => (
                    <div
                      key={column.key}
                      style={{ flex: column.flex, minWidth: column.minWidth || 150 }}
                    >
                      <Field
                        label={first ? column.label : undefined}
                        name={`${idPrefix}-${column.key}-${index}`}
                        value={row[column.key]}
                        onChange={(value) => onChange(index, { [column.key]: value })}
                        placeholder={column.placeholder}
                        rows={column.rows}
                        maxLength={column.maxLength}
                        disabled={busy}
                        invalid={Boolean(problem)}
                      />
                    </div>
                  ))}

                  <RowBin
                    onClick={() => onRemove(index)}
                    disabled={busy}
                    label={`${COPY.remove} ${row[columns[0].key] || index + 1}`}
                    topNudge={first ? LABEL_HEIGHT : 0}
                  />
                </div>

                <FieldError>{problem}</FieldError>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyNote>{empty}</EmptyNote>
      )}

      <FieldError>{showErrors ? errors.all : ''}</FieldError>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          marginTop: 16,
          flexWrap: 'wrap',
        }}
      >
        <SmallButton onClick={onAdd} disabled={busy || full} icon={<PlusGlyph />}>
          {addLabel}
        </SmallButton>

        <span style={{ fontSize: TYPE.hint, color: T.inkFaint }}>
          {full ? fullNote : footNote}
        </span>
      </div>
    </Card>
  );
}
